import { useMemo } from 'react';
import { LocationFeatureSettingsResponse } from '@weave/schema-gen-ts/dist/schemas/payments/messages/views/location_features.pb';
import { UseQueryResult, useQueries, useQuery } from 'react-query';
import { SchemaPaymentsViewsService } from '@frontend/schema';
import { useSelectedLocations } from '../../providers/selected-location-context';
import {
  paymentsLocationFeatureSettings,
  PaymentsFeatureSettings,
  PaymentFeatureSettingsValues,
  paymentFeatureSettingsWithValues,
  PaymentFeatureSettingsValuesMap,
} from '../../types/payment-hooks.types';
import { MixedBoolean } from '../../utils/mixed-values';
import { getInterScreenQueryOptions } from '../../utils/query-helpers';

export type FeatureSettingData = Omit<UseQueryResult<LocationFeatureSettingsResponse, unknown>, 'data'> & {
  enabled: boolean;
  queryKey: string[];
};

export const getLocationFeatureSettingsQueryKey = (locationId: string, settingName: PaymentsFeatureSettings) => [
  locationId,
  'feature-flags',
  settingName,
];

interface UseLocationFeatureSettingsOptions {
  locationIds?: string[];
  locationId?: string;
}

/**
 * Not safe to use everywhere, because it uses selected locations context.
 * Use with caution.
 */
export const useLocationFeatureSettings = (
  settingName: PaymentsFeatureSettings,
  { locationIds: locationIdsParam, locationId }: UseLocationFeatureSettingsOptions = {}
) => {
  const { locationIds: locationIdsFromUtils } = useSelectedLocations();
  const locationIds = (locationIdsParam ?? locationIdsFromUtils).filter(Boolean) as string[];

  return useLocationFeatureSettingsDirect(settingName, { locationIds, locationId });
};

/**
 * Prop driven. Use when you can supply the location IDs that should be used.
 */
export const useLocationFeatureSettingsDirect = (
  settingName: PaymentsFeatureSettings,
  { locationIds: locationIdsParam, locationId }: UseLocationFeatureSettingsOptions = {}
) => {
  const locationIds = locationIdsParam?.filter(Boolean) as string[];

  const featureSettingsResults = useQueries(
    locationIds.map((locationId) => ({
      queryKey: getLocationFeatureSettingsQueryKey(locationId, settingName),
      queryFn: () => SchemaPaymentsViewsService.LocationFeatureSettings({ locationId }),
      enabled: !!locationId,
      ...getInterScreenQueryOptions(),
    }))
  );

  const { featureSettingsData, isAllEnabled, isAnyEnabled, enabledLocations } = useMemo(() => {
    let isAllEnabled = featureSettingsResults.length > 0;
    let isAnyEnabled = false;
    const featureSettingsData: Record<string, FeatureSettingData> = {};
    const enabledLocations: string[] = [];

    featureSettingsResults.forEach((featureSettingResult, idx) => {
      const locationId = locationIds[idx] || '';

      const { data, ...queryResult } = featureSettingResult;
      const queryKey = getLocationFeatureSettingsQueryKey(locationId, settingName);
      const settingKey = paymentsLocationFeatureSettings[settingName];
      let enabled = false;
      if (settingKey) {
        enabled = !!((settingKey && data?.settings?.[settingKey]) ?? false);
      } else {
        console.error(`Setting key for ${settingName} not found in paymentsLocationFeatureSettings.`);
      }
      isAllEnabled = isAllEnabled && enabled;
      isAnyEnabled = isAnyEnabled || enabled;
      if (enabled) enabledLocations.push(locationId);
      featureSettingsData[locationId] = { enabled, ...queryResult, queryKey, isLoading: queryResult.isLoading };
    });

    return { featureSettingsData, isAllEnabled, isAnyEnabled, enabledLocations };
  }, [featureSettingsResults]);

  const refetchAll = () => featureSettingsResults.forEach((result) => result.refetch());

  const firstLocationId = locationIds[0] ?? '';
  return {
    featureSettingsData,
    ...featureSettingsData[firstLocationId],
    isAllEnabled,
    isAnyEnabled,
    mixedValue: (isAnyEnabled && !isAllEnabled ? 'mixed' : isAllEnabled) as MixedBoolean,
    ...(locationId ? { specifiedLocationEnabled: featureSettingsData[locationId]?.enabled } : {}),
    refetchAll,
    enabledLocations,
  };
};

export const useLocationFeatureSetting = (settingName: PaymentsFeatureSettings, locationId: string) => {
  const queryKey = getLocationFeatureSettingsQueryKey(locationId, settingName);

  const { data, ...queryResult } = useQuery({
    queryKey,

    queryFn: () => SchemaPaymentsViewsService.LocationFeatureSettings({ locationId }),

    enabled: !!locationId,

    ...getInterScreenQueryOptions(),
  });

  const settingKey = paymentsLocationFeatureSettings[settingName];

  let enabled = false;

  if (settingKey) {
    enabled = !!((settingKey && data?.settings?.[settingKey]) ?? false);
  } else {
    console.error(`Setting key for ${settingName} not found in paymentsLocationFeatureSettings.`);
  }

  return { enabled, ...queryResult, queryKey, isLoading: queryResult.isLoading };
};

/**
 * Use a traditional settingName to retrieve a coresponding, non-boolean value from the same object.
 * Not a great BE response pattern, but we go with the flow.
 */
export const useLocationFeatureSettingValue = <T extends PaymentFeatureSettingsValues>(
  settingName: T,
  { locationIds: locationIdsParam }: UseLocationFeatureSettingsOptions = {}
) => {
  const locationIds = (locationIdsParam ?? []).filter(Boolean) as string[];

  const featureSettingsResults = useQueries(
    locationIds.map((locationId) => ({
      queryKey: getLocationFeatureSettingsQueryKey(locationId, settingName),
      queryFn: () => SchemaPaymentsViewsService.LocationFeatureSettings({ locationId }),
      enabled: !!locationId,
      ...getInterScreenQueryOptions(),
    }))
  );

  const settingValueKey = paymentFeatureSettingsWithValues[settingName];

  const values = featureSettingsResults.reduce(
    (acc, featureSettingResult, idx) => {
      const locationId = locationIds[idx] || '';

      const { data, isLoading } = featureSettingResult;

      acc[locationId] = { value: data?.settings?.[settingValueKey], isLoading };

      return acc;
    },
    {} as Record<
      string,
      {
        value: PaymentFeatureSettingsValuesMap<T>;
        isLoading: boolean;
      }
    >
  );

  const valuesSafe = useMemo(() => {
    return values;
  }, [JSON.stringify(values)]);

  return valuesSafe;
};
